import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Wrench, Loader2 } from "lucide-react";
import { RepairHeader } from "@/components/RepairHeader";
import { FunctionalTriage } from "@/components/FunctionalTriage";
import { DiagnosisContent } from "@/components/DiagnosisContent";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { useWorkflowDefaults } from "@/hooks/useWorkflowDefaults";
import { useWorkOrderProgress } from "@/hooks/useWorkOrderProgress";
import { usePhaseTimer } from "@/hooks/usePhaseTimer";
import { usePhaseDefaults } from "@/hooks/usePhaseDefaults";
import { useAppliance } from "@/hooks/useAppliance";
import { useTranslation } from "react-i18next";

const ALL_TABS = ["Functional Triage", "Error Codes", "Diagnosis"];
const TAB_KEYS: Record<string, string> = {
  "Functional Triage": "triage",
  "Error Codes": "errorCodes",
  "Diagnosis": "diagnosis",
};

const RepairWorkflow = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation("repair");
  const { t: tApp } = useTranslation("appliance");
  const [activeTab, setActiveTab] = useState("Functional Triage");

  const { appliance: loadedAppliance, isLoading: isLoadingAppliance, notFound } = useAppliance(id);

  useEffect(() => {
    if (notFound) {
      toast.error(tApp("detail.notFound"));
      navigate("/work-orders");
    }
  }, [notFound, navigate, tApp]);

  // Work order progress persistence
  const { savedProgress, saveProgress } = useWorkOrderProgress(id, "repair");

  const appliance = loadedAppliance ?? {
    name: tApp("detail.loading"),
    modelNumber: "",
    category: "Appliance",
    brand: "",
  };

  // Fetch workflow defaults for repair
  const { defaults, isLoading } = useWorkflowDefaults(
    appliance.brand,
    appliance.category,
    "repair",
    appliance.modelNumber
  );

  const { isPhaseEnabled } = usePhaseDefaults(appliance.category, appliance.brand);

  // Functional Triage is always available; the other tabs follow phase defaults.
  const tabs = ALL_TABS.filter((tab) => {
    if (tab === "Error Codes") return isPhaseEnabled("error_codes", "repair");
    if (tab === "Diagnosis") return isPhaseEnabled("troubleshooting", "repair");
    return true;
  });

  // Keep activeTab valid when filtered tabs change
  useEffect(() => {
    if (!tabs.includes(activeTab) && tabs.length > 0) {
      setActiveTab(tabs[0]);
    }
  }, [tabs, activeTab]);

  // Restore saved tab from work order
  useEffect(() => {
    if (savedProgress?.currentPhaseName && tabs.includes(savedProgress.currentPhaseName)) {
      setActiveTab(savedProgress.currentPhaseName);
    }
  }, [savedProgress]);

  // Save active tab to work order
  useEffect(() => {
    const tabIndex = tabs.indexOf(activeTab);
    saveProgress({
      currentStepIndex: tabIndex,
      completedSteps: [],
      currentPhaseName: activeTab,
      currentPhaseCategory: activeTab.toLowerCase().replace(/\s+/g, "_"),
      currentStepTitle: activeTab,
      totalSteps: tabs.length,
      completedCount: tabIndex,
    });
  }, [activeTab]);

  // Anonymous phase time tracking — maps tab to a stable phase category
  const tabToPhaseCategory: Record<string, { category: string; label: string }> = {
    "Functional Triage": { category: "triage_assessment", label: "Triage Assessment" },
    "Error Codes": { category: "error_codes", label: "Common Error Codes" },
    "Diagnosis": { category: "troubleshooting", label: "Pre-repair Troubleshooting" },
  };
  const phaseInfo = tabToPhaseCategory[activeTab];
  usePhaseTimer({
    phaseCategory: phaseInfo?.category,
    phaseLabel: phaseInfo?.label,
    applianceId: id,
    applianceType: appliance.category,
    brand: appliance.brand,
  });


  const triageItems = defaults.troubleshooting.length > 0
    ? defaults.troubleshooting.map((item, idx) => ({
        id: String(idx + 1),
        description: item.title + (item.description ? ` — ${item.description}` : ""),
      }))
    : [
        { id: "1", description: "Machine powers on when plugged in" },
        { id: "2", description: "Control panel responds to button presses" },
        { id: "3", description: "Door opens and closes properly" },
        { id: "4", description: "Water fills when cycle starts" },
        { id: "5", description: "Drum rotates during wash cycle" },
      ];

  // Build recommended parts from parts_replacement workflow items
  const recommendedParts = defaults.partsReplacement.length > 0
    ? defaults.partsReplacement.map((item) => item.title)
    : ["Door Boot Seal", "Drain Pump"];

  const handleProceedToRepair = () => {
    navigate(`/appliance/${id}/repair/process`);
  };

  const handleConsiderRefurbishment = () => {
    navigate(`/appliance/${id}`);
  };

  if (isLoadingAppliance && !loadedAppliance) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-6 h-6 text-primary animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <RepairHeader
        productName={appliance.name}
        modelNumber={appliance.modelNumber}
        category={appliance.category}
      />

      <main className="p-6 max-w-5xl mx-auto">
        {/* Repair Diagnosis Card */}
        <div className="bg-card rounded-2xl border border-border p-6 mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                <Wrench className="w-6 h-6 text-primary" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-foreground">{t("workflow.diagnosisTitle")}</h2>
                <p className="text-muted-foreground">
                  {t("workflow.diagnosisSub", { name: appliance.name })}
                </p>
              </div>
            </div>
            <Button variant="outline" onClick={() => navigate(`/appliance/${id}`)}>
              {t("workflow.backToProduct")}
            </Button>
          </div>
        </div>

        {/* Tab Bar */}
        <div className="flex bg-card rounded-2xl border border-border mb-6 overflow-hidden">
          {tabs.map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${
                activeTab === tab
                  ? "bg-muted text-foreground"
                  : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
              }`}
            >
              {t(`workflow.tabs.${TAB_KEYS[tab]}`)}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="bg-card rounded-2xl border border-border p-6">
          {isLoading ? (
            <div className="flex flex-col items-center gap-3 py-8">
              <Loader2 className="w-6 h-6 text-primary animate-spin" />
              <p className="text-sm text-muted-foreground">{t("workflow.loadingData")}</p>
            </div>
          ) : (
            <>
              {activeTab === "Functional Triage" && (
                <FunctionalTriage 
                  items={triageItems} 
                  productName={appliance.name}
                  onSkipToErrorCodes={() => setActiveTab("Error Codes")}
                  onAnalyzeResults={() => setActiveTab("Diagnosis")}
                />
              )}

              {activeTab === "Error Codes" && (
                <div className="space-y-3">
                  {defaults.errorCodes.length > 0 ? (
                    defaults.errorCodes.map((ec) => (
                      <div key={ec.id} className="p-4 bg-muted/50 rounded-xl border border-border">
                        <p className="font-medium text-foreground">{ec.title}</p>
                        {ec.description && (
                          <p className="text-sm text-muted-foreground mt-1">{ec.description}</p>
                        )}
                      </div>
                    ))
                  ) : (
                    <p className="text-muted-foreground">{t("workflow.errorCodes.none", { brand: appliance.brand, category: appliance.category })}</p>
                  )}
                  {defaults.tips.length > 0 && (
                    <div className="mt-6">
                      <h4 className="text-sm font-semibold text-foreground mb-2">{t("workflow.errorCodes.tips")}</h4>
                      {defaults.tips.map((tip) => (
                        <div key={tip.id} className="p-3 bg-primary/5 rounded-lg border border-primary/10">
                          <p className="font-medium text-sm text-foreground">{tip.title}</p>
                          {tip.description && (
                            <p className="text-xs text-muted-foreground mt-1">{tip.description}</p>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {activeTab === "Diagnosis" && (
                <DiagnosisContent 
                  recommendedParts={recommendedParts}
                  onProceedToRepair={handleProceedToRepair}
                  onConsiderRefurbishment={handleConsiderRefurbishment}
                />
              )}
            </>
          )}
        </div>
      </main>
    </div>
  );
};

export default RepairWorkflow;
